import { Navigate, Outlet } from "react-router-dom";
import { usePermissions } from "@/shared/hooks/usePermissions";
import type { Module } from "@/config/roles";

interface ModuleGuardProps {
  /** The module this route belongs to */
  module: Module;
  /** Minimum access level required. Defaults to "read". */
  level?: "read" | "write";
  /** Where to redirect if the user lacks access. Defaults to "/" */
  redirectTo?: string;
}

/**
 * ModuleGuard
 *
 * Drop-in route guard that restricts access to a module at the navigation level.
 * Renders <Outlet /> when access is granted, redirects otherwise.
 *
 * Usage in routes.tsx:
 *   {
 *     element: <ModuleGuard module="announcements" />,
 *     children: [{ path: "/announcements", element: <AnnouncementManagementPage /> }],
 *   }
 */
export function ModuleGuard({
  module,
  level = "read",
  redirectTo = "/",
}: ModuleGuardProps) {
  const { canRead, canWrite } = usePermissions();
  const allowed = level === "write" ? canWrite(module) : canRead(module);
  return allowed ? <Outlet /> : <Navigate to={redirectTo} replace />;
}
